import { Link } from './Link';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { LegalConfig } from '../lib/types';
import { useCookieConsent } from '../lib/cookieConsent';
import { useFeatures, type FeatureKind } from '../lib/useFeatures';
import { useLegalPagesVisible } from '../lib/useLegalPagesVisible';
import { socialLinkList } from '../lib/socialLinks';
import { Logo, Wordmark } from './Brand';
import type { CSSProperties } from 'react';

/** Feature marketing links in the footer, gated by the global feature flag. */
const FEATURE_LINKS: ReadonlyArray<{ kind: FeatureKind; to: string; label: string }> = [
  { kind: 'domain_health', to: '/features/domain-health', label: 'Domain health' },
  { kind: 'inbox_placement', to: '/features/inbox-placement', label: 'Inbox placement' },
  { kind: 'warmup_inbox', to: '/features/warmup', label: 'Mailbox warmup' },
  { kind: 'phone_validation', to: '/features/phone-validation', label: 'Phone validation' },
  { kind: 'blacklist', to: '/features/blacklist-checks', label: 'Blacklist monitoring' },
];

/**
 * Strip a configured `websiteUrl` down to just its host (drops the protocol and
 * any trailing path/query/hash) so the footer shows a compact `sieveguard.com`
 * rather than `https://sieveguard.com/`. Falls back to the raw string on parse
 * errors — invalid input still shows something instead of blowing up.
 */
function prettyDomain(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '';
  try {
    return new URL(trimmed).host.replace(/^www\./, '');
  } catch {
    return trimmed.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  }
}

/**
 * Shared footer for every public page. Four-column layout (Product / Resources / Legal /
 * Company) plus a bottom row with the business identity pulled live from /legal/config
 * — so the super-admin can change VAT / address / entity name without a redeploy.
 *
 * Renders sane placeholders when the config hasn't been filled in yet, with a clear
 * "[set in Admin → Settings → Legal]" hint so it's obvious which fields are stubs.
 * The operator can flip `hideBusinessIdentityInFooter` in Admin → Settings → Public
 * site to swap the full identity block for a minimal domain line (or nothing).
 */
export function PublicFooter() {
  const { data: legal } = useQuery({
    queryKey: ['legal-config'],
    queryFn: () => api<LegalConfig>('/legal/config', { auth: false }),
    staleTime: 60 * 60 * 1000, // 1h — public config rarely changes
    // The footer is on every public page, so refetching here on mount picks up super-admin toggles
    // (e.g. hiding /about) on the NEXT page load without a redeploy, and — because all the
    // ['legal-config'] observers share one cache — propagates the live value to useLegal /
    // useSocialLinks / the About-page gate too. The seeded SSG snapshot still paints instantly;
    // this just reconciles it to live config a beat later (bounded by the endpoint's 5-min CDN TTL).
    refetchOnMount: 'always',
  });
  const { feature } = useFeatures();
  const { isVisible: legalOn } = useLegalPagesVisible();
  const year = new Date().getFullYear();
  const socials = socialLinkList(legal?.socialLinks);

  // sg-defer: shared by every public page and always below the fold, so the browser skips its
  // layout and paint until it is scrolled to. See .sg-defer in index.css for why the intrinsic
  // sizes are measured values rather than guesses.
  return (
    <footer
      className="sg-defer mt-20 border-t border-bordersoft pt-10 pb-10 text-[13px]"
      style={{ '--sg-defer-h': '1860px', '--sg-defer-h-md': '633px' } as CSSProperties}
    >
      <div className="grid gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
        <FooterCol title="Product">
          <FooterLink to="/">Home</FooterLink>
          <FooterLink to="/pricing">Pricing</FooterLink>
          {feature('agency') && <FooterLink to="/agencies">For agencies</FooterLink>}
          {feature('affiliate') && <FooterLink to="/partners">For partners</FooterLink>}
          <FooterLink to="/docs">API docs</FooterLink>
          <FooterExternal href="/api/docs">OpenAPI reference ↗</FooterExternal>
        </FooterCol>

        <FooterCol title="Features">
          <FooterLink to="/features">Overview</FooterLink>
          {/* Core product — never flag-gated, so it can't live in FEATURE_LINKS (every entry
              there needs a `kind` for the `feature()` filter). Listing it explicitly: it is the
              highest-priority feature page in the sitemap (0.95), and leaving it out gave the
              MOST important feature page zero footer links while every add-on page got one on
              all 37 prerendered pages. */}
          <FooterLink to="/features/bot-detection">Bot detection &amp; verification</FooterLink>
          {FEATURE_LINKS.filter((f) => feature(f.kind)).map((f) => (
            <FooterLink key={f.kind} to={f.to}>
              {f.label}
            </FooterLink>
          ))}
        </FooterCol>

        <FooterCol title="Resources">
          {/* /learn is the informational layer. It needs a link from every page for two reasons:
              internal links are how crawl priority reaches a brand-new section, and the site-wide
              SEO contract fails any route nothing else links to. */}
          <FooterLink to="/learn">Learn</FooterLink>
          <FooterLink to="/docs">Quickstart</FooterLink>
          <FooterLink to="/docs#bulk">Async bulk ingest</FooterLink>
          <FooterLink to="/docs#webhooks">Webhooks</FooterLink>
          <FooterLink to="/register">Get an API key</FooterLink>
        </FooterCol>

        <FooterCol title="Free tools">
          <FooterLink to="/tools">All tools →</FooterLink>
          <FooterLink to="/tools/spf-checker">SPF checker</FooterLink>
          <FooterLink to="/tools/dkim-checker">DKIM checker</FooterLink>
          <FooterLink to="/tools/dmarc-checker">DMARC checker</FooterLink>
          <FooterLink to="/tools/domain-checker">Domain checker</FooterLink>
          <FooterLink to="/tools/email-spam-checker">Email spam checker</FooterLink>
          <FooterLink to="/tools/spam-words-checker">Spam words checker</FooterLink>
          <FooterLink to="/tools/blacklist-checker">Blacklist checker</FooterLink>
          <FooterLink to="/tools/smtp-test">SMTP test</FooterLink>
          <FooterLink to="/tools/spf-generator">SPF generator</FooterLink>
          <FooterLink to="/tools/dkim-generator">DKIM generator</FooterLink>
          <FooterLink to="/tools/dmarc-generator">DMARC generator</FooterLink>
          <FooterLink to="/tools/subject-line-tester">Subject line tester</FooterLink>
          <FooterLink to="/tools/subject-line-generator">Subject line generator</FooterLink>
          <FooterLink to="/tools/ai-email-writer">AI email writer</FooterLink>
          <FooterLink to="/tools/email-shortener">Email shortener</FooterLink>
        </FooterCol>

        <FooterCol title="Legal">
          <FooterLink to="/legal">Legal overview</FooterLink>
          {legalOn('privacy') && <FooterLink to="/privacy">Privacy Policy</FooterLink>}
          {legalOn('terms') && <FooterLink to="/terms">Terms of Use</FooterLink>}
          {legalOn('dpa') && <FooterLink to="/dpa">DPA</FooterLink>}
          {legalOn('gdpr') && <FooterLink to="/gdpr">GDPR compliance</FooterLink>}
          {legalOn('aup') && <FooterLink to="/aup">Acceptable Use</FooterLink>}
          {legalOn('subprocessors') && <FooterLink to="/subprocessors">Sub-processors</FooterLink>}
          {legalOn('cookies') && <FooterLink to="/cookies">Cookie Policy</FooterLink>}
          <CookieSettingsLink />
        </FooterCol>

        <FooterCol title="Company">
          {legal?.aboutPageEnabled !== false && <FooterLink to="/about">About</FooterLink>}
          {legalOn('contact') && <FooterLink to="/contact">Contact</FooterLink>}
          <FooterLink to="/login">Sign in</FooterLink>
          <FooterLink to="/register">Register</FooterLink>
        </FooterCol>
      </div>

      <div className="mt-10 flex flex-col gap-4 border-t border-bordersoft pt-6 font-mono text-[11.5px] text-faint sm:flex-row sm:flex-wrap sm:items-center">
        <Link to="/" className="flex items-center gap-2">
          <Logo size={28} />
          <Wordmark className="text-base" />
        </Link>
        <div className="min-w-0 flex-1 leading-relaxed">
          {/* Three-way rendering:
              1. hideBusinessIdentityInFooter=true → show a compact website line (or
                 nothing but the logo/copyright if websiteUrl also unset). Matches
                 the common Maltese sole-trader practice of not disclosing personal
                 identity details on every public page.
              2. Identity configured  → show the full legal block.
              3. Identity NOT configured → show the "not yet configured" hint so
                 the operator can spot the empty state during setup. Only visible
                 while the hide flag is off. */}
          {legal?.hideBusinessIdentityInFooter ? (
            legal?.websiteUrl ? (
              <span className="text-muted">{prettyDomain(legal.websiteUrl)}</span>
            ) : null
          ) : legal?.legalEntityName ? (
            // Stack each detail on its own line on mobile; inline with " · "
            // separators from sm up.
            <span className="flex flex-col gap-0.5 sm:block">
              <span className="text-muted">{legal.legalEntityName}</span>
              {legal.vatNumber && (
                <span>
                  <span className="hidden sm:inline">{' · '}</span>VAT {legal.vatNumber}
                </span>
              )}
              {legal.registrationNumber && (
                <span>
                  <span className="hidden sm:inline">{' · '}</span>Reg. {legal.registrationNumber}
                </span>
              )}
              {legal.registeredAddress && (
                <span>
                  <span className="hidden sm:inline">{' · '}</span>
                  {[
                    legal.registeredAddress.street,
                    legal.registeredAddress.city,
                    legal.registeredAddress.postalCode,
                    legal.registeredAddress.country,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              )}
            </span>
          ) : (
            <span className="italic">
              Business identity not yet configured — set in Admin → Settings → Legal.
            </span>
          )}
        </div>
        {socials.length > 0 && (
          <nav aria-label="SieveGuard on social media" className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {socials.map((s) => (
              <a
                key={s.key}
                href={s.url}
                target="_blank"
                // rel="me" doubles as an identity-verification signal (rel-me) alongside the
                // Organization.sameAs schema; noopener/noreferrer for the external target.
                rel="me noopener noreferrer"
                className="text-muted transition hover:text-accent"
              >
                {s.label}
              </a>
            ))}
          </nav>
        )}
        <div>© {year} SieveGuard. All rights reserved.</div>
      </div>
    </footer>
  );
}

function FooterCol({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.16em] text-faint">
        {title}
      </div>
      <ul className="flex flex-col gap-2">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      {/* `Link` here is components/Link — it normalises to the trailing-slash href. The footer
          renders on all 37 prerendered pages, so its ~38 links were the bulk of the site's
          internal hrefs pointing at a 301. */}
      <Link to={to} className="text-muted transition hover:text-accent">
        {children}
      </Link>
    </li>
  );
}

/** Re-opens the cookie-consent preferences panel (GDPR: consent must be withdrawable anytime). */
function CookieSettingsLink() {
  const openSettings = useCookieConsent((s) => s.openSettings);
  return (
    <li>
      <button
        type="button"
        onClick={openSettings}
        className="text-left text-muted transition hover:text-accent"
      >
        Cookie settings
      </button>
    </li>
  );
}

function FooterExternal({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <li>
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-muted transition hover:text-accent"
      >
        {children}
      </a>
    </li>
  );
}
